import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Calendar, MapPin, Clock, User, Video, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function MatchScheduler({ user, setUser }) {
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scorers, setScorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLeague, setFilterLeague] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [deletingMatch, setDeletingMatch] = useState(null);
  const [formData, setFormData] = useState({
    league_id: '',
    team_a_id: '',
    team_b_id: '',
    match_date_time: '',
    ground: '',
    overs: 20,
    status: 'scheduled',
    assigned_scorer_user_id: '',
    live_stream_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [filteredTeams, setFilteredTeams] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchMatches();
  }, [filterStatus, filterLeague]);

  useEffect(() => {
    if (formData.league_id) {
      setFilteredTeams(teams.filter(t => t.league_id === formData.league_id));
    } else {
      setFilteredTeams([]);
    }
  }, [formData.league_id, teams]);

  const fetchData = async () => {
    try {
      const [matchesRes, leaguesRes, teamsRes, scorersRes] = await Promise.all([
        axios.get(`${API}/matches`),
        axios.get(`${API}/leagues`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/users/scorers`)
      ]);
      setMatches(matchesRes.data);
      setLeagues(leaguesRes.data);
      setTeams(teamsRes.data);
      setScorers(scorersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterLeague !== 'all') params.league_id = filterLeague;
      const response = await axios.get(`${API}/matches`, { params });
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const handleOpenDialog = (match = null) => {
    if (match) {
      setEditingMatch(match);
      setFormData({
        league_id: match.league_id,
        team_a_id: match.team_a_id,
        team_b_id: match.team_b_id,
        match_date_time: match.match_date_time.slice(0, 16),
        ground: match.ground,
        overs: match.overs,
        status: match.status,
        assigned_scorer_user_id: match.assigned_scorer_user_id || '',
        live_stream_url: match.live_stream_url || ''
      });
    } else {
      setEditingMatch(null);
      setFormData({
        league_id: filterLeague !== 'all' ? filterLeague : '',
        team_a_id: '',
        team_b_id: '',
        match_date_time: '',
        ground: '',
        overs: 20,
        status: 'scheduled',
        assigned_scorer_user_id: '',
        live_stream_url: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validation
    if (formData.team_a_id === formData.team_b_id) {
      toast.error('Team A and Team B must be different');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        assigned_scorer_user_id: formData.assigned_scorer_user_id || null
      };

      if (editingMatch) {
        const { league_id, team_a_id, team_b_id, ...updateData } = payload;
        await axios.put(`${API}/matches/${editingMatch.match_id}`, updateData);
        toast.success('Match updated successfully');
      } else {
        await axios.post(`${API}/matches`, payload);
        toast.success('Match scheduled successfully');
      }
      setDialogOpen(false);
      fetchMatches();
    } catch (error) {
      console.error('Error saving match:', error);
      toast.error(error.response?.data?.detail || 'Failed to save match');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingMatch) return;

    try {
      await axios.delete(`${API}/matches/${deletingMatch.match_id}`);
      toast.success('Match deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingMatch(null);
      fetchMatches();
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error('Failed to delete match');
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.name || 'Unknown Team';
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.league_id === leagueId);
    return league?.name || 'Unknown League';
  };

  const getScorerName = (userId) => {
    const scorer = scorers.find(s => s.user_id === userId);
    return scorer?.name || 'Not Assigned';
  };

  const scheduledMatches = matches.filter(m => m.status === 'scheduled');
  const liveMatches = matches.filter(m => m.status === 'live');
  const completedMatches = matches.filter(m => m.status === 'completed');

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Match Scheduler</h1>
              <p className="text-slate-500 mt-1">Schedule and manage matches</p>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              disabled={leagues.length === 0}
              data-testid="schedule-match-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Match
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <Select value={filterLeague} onValueChange={setFilterLeague}>
              <SelectTrigger className="w-48 rounded-xl" data-testid="filter-league">
                <SelectValue placeholder="Filter by league" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leagues</SelectItem>
                {leagues.map(league => (
                  <SelectItem key={league.league_id} value={league.league_id}>
                    {league.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Matches Tabs */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton h-32 rounded-2xl"></div>
              ))}
            </div>
          ) : leagues.length === 0 ? (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <Calendar className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Create a league first</h3>
              <p className="text-slate-500 mt-2">You need leagues and teams before scheduling matches</p>
            </div>
          ) : (
            <Tabs defaultValue="scheduled" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 rounded-xl bg-slate-100 p-1">
                <TabsTrigger value="scheduled" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  Scheduled ({scheduledMatches.length})
                </TabsTrigger>
                <TabsTrigger value="live" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  Live ({liveMatches.length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  Completed ({completedMatches.length})
                </TabsTrigger>
              </TabsList>

              {['scheduled', 'live', 'completed'].map((status) => {
                const filteredMatches = status === 'scheduled' ? scheduledMatches : 
                                        status === 'live' ? liveMatches : completedMatches;
                return (
                  <TabsContent key={status} value={status}>
                    {filteredMatches.length > 0 ? (
                      <div className="space-y-4">
                        {filteredMatches.map((match) => (
                          <Card key={match.match_id} className="glass-card rounded-2xl" data-testid={`match-${match.match_id}`}>
                            <CardContent className="p-6">
                              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-3">
                                    <Badge className={
                                      match.status === 'live' ? 'badge-live' :
                                      match.status === 'scheduled' ? 'badge-scheduled' :
                                      'badge-completed'
                                    }>
                                      {match.status.toUpperCase()}
                                    </Badge>
                                    <span className="text-sm text-slate-500">{match.overs} overs</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-4 text-lg font-semibold">
                                    <span>{getTeamName(match.team_a_id)}</span>
                                    <span className="text-slate-400">vs</span>
                                    <span>{getTeamName(match.team_b_id)}</span>
                                  </div>

                                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4" />
                                      {getLeagueName(match.league_id)}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-4 h-4" />
                                      {formatDateTime(match.match_date_time)}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="w-4 h-4" />
                                      {match.ground}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4" />
                                      Scorer: {getScorerName(match.assigned_scorer_user_id)}
                                    </div>
                                    {match.live_stream_url && (
                                      <div className="flex items-center gap-2 text-red-600">
                                        <Video className="w-4 h-4" />
                                        <span className="text-xs">Live Stream Added</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-lg"
                                    onClick={() => handleOpenDialog(match)}
                                    data-testid={`edit-match-${match.match_id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                    onClick={() => {
                                      setDeletingMatch(match);
                                      setDeleteDialogOpen(true);
                                    }}
                                    data-testid={`delete-match-${match.match_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state glass-card rounded-2xl">
                        <div className="empty-state-icon">
                          <Calendar className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          No {status} matches
                        </h3>
                        <p className="text-slate-500 mt-2">
                          {status === 'scheduled' ? 'Schedule your first match to get started' : 
                           status === 'live' ? 'No matches are currently live' : 
                           'No matches have been completed yet'}
                        </p>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMatch ? 'Edit Match' : 'Schedule New Match'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {!editingMatch && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="league">League *</Label>
                    <Select 
                      value={formData.league_id} 
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        league_id: value,
                        team_a_id: '',
                        team_b_id: ''
                      }))}
                    >
                      <SelectTrigger data-testid="match-league-select">
                        <SelectValue placeholder="Select a league" />
                      </SelectTrigger>
                      <SelectContent>
                        {leagues.map(league => (
                          <SelectItem key={league.league_id} value={league.league_id}>
                            {league.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="team_a">Team A *</Label>
                      <Select 
                        value={formData.team_a_id} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, team_a_id: value }))}
                        disabled={!formData.league_id}
                      >
                        <SelectTrigger data-testid="match-team-a-select">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredTeams.map(team => (
                            <SelectItem 
                              key={team.team_id} 
                              value={team.team_id}
                              disabled={team.team_id === formData.team_b_id}
                            >
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="team_b">Team B *</Label>
                      <Select 
                        value={formData.team_b_id} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, team_b_id: value }))}
                        disabled={!formData.league_id}
                      >
                        <SelectTrigger data-testid="match-team-b-select">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredTeams.map(team => (
                            <SelectItem 
                              key={team.team_id} 
                              value={team.team_id}
                              disabled={team.team_id === formData.team_a_id}
                            >
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="match_date_time">Date & Time *</Label>
                <Input
                  id="match_date_time"
                  type="datetime-local"
                  value={formData.match_date_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, match_date_time: e.target.value }))}
                  required
                  data-testid="match-datetime-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ground">Venue/Ground *</Label>
                <Input
                  id="ground"
                  value={formData.ground}
                  onChange={(e) => setFormData(prev => ({ ...prev, ground: e.target.value }))}
                  placeholder="e.g., Central Park Cricket Ground"
                  required
                  data-testid="match-ground-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="overs">Overs *</Label>
                  <Input
                    id="overs"
                    type="number"
                    min="1"
                    max="50"
                    value={formData.overs}
                    onChange={(e) => setFormData(prev => ({ ...prev, overs: parseInt(e.target.value) || 20 }))}
                    required
                    data-testid="match-overs-input"
                  />
                </div>
                {editingMatch && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger data-testid="match-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="scorer">Assign Scorer</Label>
                <Select 
                  value={formData.assigned_scorer_user_id || "none"} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_scorer_user_id: value === "none" ? "" : value }))}
                >
                  <SelectTrigger data-testid="match-scorer-select">
                    <SelectValue placeholder="Select a scorer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Assigned</SelectItem>
                    {scorers.map(scorer => (
                      <SelectItem key={scorer.user_id} value={scorer.user_id}>
                        {scorer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {scorers.length === 0 && (
                  <p className="text-xs text-slate-500">No scorers available. Assign scorer role to users first.</p>
                )}
              </div>

              {/* YouTube Live Stream URL */}
              <div className="space-y-2">
                <Label htmlFor="live_stream_url" className="flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-500" />
                  YouTube Live Stream URL
                </Label>
                <Input
                  id="live_stream_url"
                  value={formData.live_stream_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, live_stream_url: e.target.value }))}
                  placeholder="https://youtube.com/live/xxxxx or https://youtu.be/xxxxx"
                  data-testid="match-stream-url-input"
                />
                <p className="text-xs text-slate-500">
                  Paste your YouTube Live URL to embed the stream on the match page. Viewers will see live video with your score overlay.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800"
                disabled={submitting || (!editingMatch && (!formData.league_id || !formData.team_a_id || !formData.team_b_id))}
                data-testid="save-match-btn"
              >
                {submitting ? 'Saving...' : (editingMatch ? 'Update' : 'Schedule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Match?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this match. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-match"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
